import React, { useCallback, useRef } from 'react';
import { useFocusEffect, useRouter } from 'expo-router';
import { Platform, StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { useA11ySelector } from '@sg/a11y-helpers';
import {
  BodyText,
  Icon,
  LoadingAnimation,
  ModalDialogue,
  scrollElementIntoView,
  theme,
  useNavbarContainerHeight,
  useResponsive,
  useIsFocused,
  useToggleState,
} from '@sg/garnish';
import { LoyaltyHomeProgressBar, LoyaltyRewardTerms } from '@sg/shared-fe-features-loyalty';

import {
  Loyalty,
  LOYALTY_PALETTES,
  useLoyaltyFeaturedReward,
  useNextUnlockableReward,
} from '@order/features-loyalty';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { AppFooter, ModalNavbarBackButton, SignedOutView } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useNavigateToLastInteractedStore } from '../../context/LastInteractedStore';
import { useLoyaltyContent, useLoyaltyPremiumStatus } from '../../hooks';
import { JoinSgRewardsScreen } from '../JoinSgRewardsScreen';
import {
  LoyaltyHomeChallenges,
  LoyaltyHomeRewards,
  LoyaltyHowItWorksModal,
  LoyaltyPointHistoryModal,
} from './components';
import {
  useHomeProgressBenefit,
  useLoyaltyHomeScreenReferrer,
  useLoyaltyInfo,
  useTermsBeingShown,
} from './hooks';
import { useOptInChallengeOnFocus } from './hooks/useOptInChallengeOnFocus';

// ─────────────────────────────────────────────────────────────────────────────

export const LoyaltyHomeScreen = (props: Props) => {
  const { variation = 'tab' } = props;

  const isLoggedIn = useIsLoggedIn();

  // ─── Effects ───────────────────────────────────────────────────────────────

  useTrackEffect('loyalty.home.view');
  useLoyaltyHomeScreenReferrer();

  // ───────────────────────────────────────────────────────────────────────────

  if (isLoggedIn) {
    return <LoyaltyHomeScreenSignedIn variation={variation} />;
  }

  return (
    <>
      <SignedOutView />

      {variation === 'modal' ? <ModalNavbarBackButton /> : null}
    </>
  );
};

const LoyaltyHomeScreenSignedIn = (props: SignedInProps) => {
  const { variation } = props;

  const { currentBreakpoint, minWidth } = useResponsive();
  const isScreenFocused = useIsFocused();
  const { height: navbarHeight, paddingBottomStyle: navbarPaddingBottomStyle } =
    useNavbarContainerHeight();

  const rewardsBarContainerA11ySelector = useA11ySelector('loyalty.rewards-bar.container');

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const isForgotToScanEnabled = useFeatureFlag('CELS-4258-forgot-to-scan-enabled');
  const isScanToPayLoyaltyEntryEnabled = useFeatureFlag('CELS-5108-scan-to-pay-loyalty-entry-enabled'); // prettier-ignore
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');
  const isFeaturedRewardHeroEnabled = useFeatureFlag(
    'CELS-5300-order-loyalty-featured-reward-hero-enabled',
  );

  // ─── Auto Opt In ───────────────────────────────────────────────────────────

  useOptInChallengeOnFocus();

  // ─── Premium ───────────────────────────────────────────────────────────────

  const { isPremium } = useLoyaltyPremiumStatus();
  const premiumBgColor = isPremium ? LOYALTY_PALETTES.premiumBgColor : null;
  const premiumFgColor = isPremium ? LOYALTY_PALETTES.premiumFgColor : null;
  const premiumBorder = isPremium ? styles.premiumBorder : null;

  // ─── GraphQL ───────────────────────────────────────────────────────────────

  const {
    isLoading: isLoyaltyInfoLoading,
    isStale: isLoyaltyInfoStale,
    hasFailedToLoad,
    hasNoPointsRewards,
    firstName,
    availablePoints,
    pendingPoints,
    totalPoints,
    customerId,
    challenges,
    rewards,
    featuredReward,
    pointsRewards,
    isOptedOut,
    refetch: refetchLoyaltyInfo,
  } = useLoyaltyInfo({
    pause: !isScreenFocused,
    isFeaturedRewardHeroEnabled,
  });

  const hasChallenges = challenges.length > 0;
  const hasRewards = rewards.length > 0;
  const nextReward = useNextUnlockableReward({ availablePoints, pointsRewards });

  // ─── Featured Reward ──────────────────────────────────────────────────────

  const {
    hide: hideFeaturedReward,
    isLoading: isFeaturedRewardLoading,
    isVisible: isFeaturedRewardVisible,
  } = useLoyaltyFeaturedReward({ customerId, rewardId: featuredReward?.id });

  // ─── Contentful ───────────────────────────────────────────────────────────

  const {
    isLoadingContent,
    featuredRewardContent,
    faqTitleXs,
    faqSubtitleXs,
    faqTitleSm,
    faqSubtitleSm,
    faqLoyaltyHome,
    returnHeroTitle,
    returnHeroImage,
    returnHeroImageAlt,
    homeHowItWorksSM,
    homeHowItWorksDisclaimer,
    homeProgressBenefits,
    homeProgressBenefitsDisclaimer: benefitsDisclaimer,
    termsAndConditionsUrl,
    loyaltyBenefitsTitle,
    loyaltyBenefitsSubtitle,
    unlockableRewardsTitle,
    unlockableRewardsSubtitle,
  } = useLoyaltyContent({
    availablePoints,
    nextReward,
  });

  // ─── Progress Benefit ─────────────────────────────────────────────────────

  const progressBenefit = useHomeProgressBenefit({
    availablePoints,
    pendingPoints,
    homeProgressBenefits,
  });

  // ─── Points History Modal ─────────────────────────────────────────────────

  const {
    value: isShowingPointsHistoryModal,
    toggleOn: showPointsHistoryModalToggleOn,
    toggleOff: hidePointsHistoryModal,
  } = useToggleState();

  // ─── How it Works Modal ───────────────────────────────────────────────────

  const {
    value: isShowingHowItWorksModal,
    toggleOn: showHowItWorksModalToggleOn,
    toggleOff: hideHowItWorksModal,
  } = useToggleState();

  // ─── Telemetry ────────────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const showPointsHistoryModal = useCallback(() => {
    track('loyalty.pointhistory_viewed');
    showPointsHistoryModalToggleOn();
  }, [track, showPointsHistoryModalToggleOn]);

  const showHowItWorksModal = useCallback(() => {
    track('loyalty.howitworks.cta_tapped');
    showHowItWorksModalToggleOn();
  }, [track, showHowItWorksModalToggleOn]);

  const trackBenefitsOpened = useCallback(() => {
    track('loyalty.accordion_pressed');
  }, [track]);

  const trackOnLinkPress = useCallback(() => {
    track('reward.terms.cta_tapped');
  }, [track]);

  // ─── Terms Modal ──────────────────────────────────────────────────────────

  const { termsBeingShown, showTerms, hideTerms } = useTermsBeingShown();

  // ─── Navigation ───────────────────────────────────────────────────────────

  const router = useRouter();
  const navigateToLastInteractedStore = useNavigateToLastInteractedStore({
    isInsideModal: variation === 'modal',
  });

  const clearModals = useCallback(() => {
    hideTerms();
    hidePointsHistoryModal();
    hideHowItWorksModal();
  }, [hideHowItWorksModal, hidePointsHistoryModal, hideTerms]);

  const startOrder = useCallback(() => {
    track('reward.start.cta_tapped');
    clearModals();
    navigateToLastInteractedStore();
  }, [clearModals, navigateToLastInteractedStore, track]);

  const orderFromFeaturedReward = useCallback(() => {
    hideFeaturedReward();
    startOrder();
  }, [hideFeaturedReward, startOrder]);

  const showFeaturedRewardTerms = useCallback(() => {
    if (!featuredReward) return;

    showTerms(featuredReward)();
  }, [featuredReward, showTerms]);

  const goToScanToPay = useCallback(() => {
    track('scan.tapped_rewardshome');

    router.navigate('/scan-at-checkout');
  }, [router, track]);

  const goToForgotToScan = useCallback(() => {
    track('forgot-to-scan.cta_tapped', { location: 'loyalty' });

    router.navigate('/forgot-to-scan');
  }, [router, track]);

  useFocusEffect(
    useCallback(() => {
      return clearModals;
    }, [clearModals]),
  );

  // ─── Scrolling ─────────────────────────────────────────────────────────────

  const rewardsTargetRef = useRef<View>(null);
  const handleViewRewards = useCallback(() => {
    scrollElementIntoView(rewardsTargetRef);
  }, []);

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldApplyTabNavbarPadding =
    variation === 'tab' && isNavbarV2Enabled && currentBreakpoint.isXS;
  const shouldApplyModalNavbarPadding =
    variation === 'modal' && isNavbarV2Enabled && currentBreakpoint.isXS;
  const hasTopSafeArea =
    variation === 'tab' ||
    (Platform.OS === 'android' && isNavbarV2Enabled && currentBreakpoint.isXS);
  const shouldRenderModalNavbarBackButton = variation === 'modal';

  // ─── Styles ────────────────────────────────────────────────────────────────

  const faqStyleXS = useStyle(
    () =>
      shouldApplyModalNavbarPadding
        ? { paddingBottom: theme.spacing['10'] + navbarHeight }
        : undefined,
    [navbarHeight, shouldApplyModalNavbarPadding],
  );

  const rewardsStyleXS = [
    premiumBgColor,
    styles.challengesRewardsPaddingXS,
    styles.paddingWithContentBottom,
  ];

  const challengesStyleXS = [
    premiumBgColor,
    styles.challengesRewardsPaddingXS,
    hasRewards ? styles.paddingWithContentTop : undefined,
  ];

  const benefitsStyle = [
    styles.loyaltyInfoBenefits,
    isPremium ? styles.premiumBenefitsBorder : undefined,
  ];

  const featuredRewardNavbarPaddingStyle = [
    styles.featuredRewardNavbarPadding,
    navbarPaddingBottomStyle,
  ];

  // ──────────────────────────────────────────────────────────────────────────

  if (isOptedOut) {
    return (
      <>
        <JoinSgRewardsScreen />

        {shouldRenderModalNavbarBackButton ? <ModalNavbarBackButton /> : null}
      </>
    );
  }

  if (hasFailedToLoad || hasNoPointsRewards) {
    return (
      <>
        <Loyalty.Home.ErrorState
          isLoading={isLoyaltyInfoLoading || isLoyaltyInfoStale}
          refetch={refetchLoyaltyInfo}
        />
        {minWidth.isSM ? <AppFooter /> : null}

        {shouldRenderModalNavbarBackButton ? <ModalNavbarBackButton /> : null}
      </>
    );
  }

  if (isLoyaltyInfoLoading || isLoadingContent) return <LoadingAnimation />;

  if (currentBreakpoint.isXS && featuredReward && isFeaturedRewardLoading) {
    return <LoadingAnimation />;
  }

  if (currentBreakpoint.isXS && featuredReward && isFeaturedRewardVisible) {
    return (
      <>
        <Loyalty.Home.FeaturedRewardHero
          content={featuredRewardContent}
          title={featuredReward.name}
          variant="vertical"
          onContinue={hideFeaturedReward}
          onOrderToRedeem={orderFromFeaturedReward}
          onShowTerms={showFeaturedRewardTerms}
        />

        {shouldApplyTabNavbarPadding ? (
          <View style={featuredRewardNavbarPaddingStyle} />
        ) : undefined}

        <ModalDialogue visible={Boolean(termsBeingShown)} onRequestClose={hideTerms}>
          {termsBeingShown?.terms && termsBeingShown?.name ? (
            <LoyaltyRewardTerms
              title={termsBeingShown.name}
              terms={termsBeingShown.terms}
              expirationDate={termsBeingShown?.expirationDate}
            />
          ) : undefined}
        </ModalDialogue>
      </>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <>
      <Loyalty.Home.Container
        availablePoints={availablePoints}
        hasTopSafeArea={hasTopSafeArea}
        palette={isPremium ? 'dark-kale' : 'cream'}
        style={premiumBgColor}
      >
        {minWidth.isSM && featuredReward ? (
          <Loyalty.Home.FeaturedRewardHero
            content={featuredRewardContent}
            title={featuredReward.name}
            variant="landscape"
            onOrderToRedeem={orderFromFeaturedReward}
            onShowTerms={showFeaturedRewardTerms}
          />
        ) : undefined}

        {minWidth.isSM && featuredReward ? (
          <Loyalty.Home.HowItWorksSection
            entries={homeHowItWorksSM}
            disclaimer={homeHowItWorksDisclaimer}
            isPremium={isPremium}
            termsAndConditionsUrl={termsAndConditionsUrl}
            onTermsLinkPress={trackOnLinkPress}
          />
        ) : undefined}

        {minWidth.isSM && !featuredReward ? (
          <Loyalty.Home.ReturnHero
            palette={isPremium ? 'dark' : 'light'}
            title={returnHeroTitle}
            backgroundImage={returnHeroImage}
            backgroundImageAlt={returnHeroImageAlt}
            firstName={firstName}
            availablePoints={availablePoints}
            totalPoints={totalPoints}
            pointsRewards={pointsRewards}
            onViewRewards={handleViewRewards}
            onOrderToEarn={startOrder}
          />
        ) : null}

        {/* Header */}
        {!minWidth.isSM ? (
          <Loyalty.Home.Header.Container>
            <Loyalty.Home.Header.Content>
              <Loyalty.Home.Header.FirstName firstName={firstName} style={premiumFgColor} />

              <Loyalty.Home.Header.PointsContainer>
                <Loyalty.Home.Header.AvailablePoints
                  availablePoints={availablePoints}
                  style={premiumFgColor}
                />

                {minWidth.isSM && isPremium ? (
                  <Icon name="IconGoat" width={60} height={60} />
                ) : null}
              </Loyalty.Home.Header.PointsContainer>
            </Loyalty.Home.Header.Content>

            <View style={styles.headerActions}>
              {currentBreakpoint.isXS && isPremium ? (
                <Icon name="IconGoat" width={60} height={60} />
              ) : null}

              {currentBreakpoint.isXS && isScanToPayLoyaltyEntryEnabled ? (
                <Loyalty.Home.ScanButton onPress={goToScanToPay} />
              ) : null}
            </View>
          </Loyalty.Home.Header.Container>
        ) : null}

        {/* Benefits & Progress Bar */}

        {pointsRewards.length > 0 && !minWidth.isSM ? (
          <LoyaltyHomeProgressBar
            onlyTerminalDots
            palette={isPremium ? 'dark' : 'light'}
            availablePoints={availablePoints}
            totalPoints={totalPoints}
            pointsRewards={pointsRewards}
            {...rewardsBarContainerA11ySelector.props}
          />
        ) : null}

        {progressBenefit && pointsRewards.length > 0 && !minWidth.isSM ? (
          <Loyalty.Home.ProgressBenefit
            palette={isPremium ? 'dark' : 'light'}
            title={loyaltyBenefitsTitle}
            subtitle={loyaltyBenefitsSubtitle}
            benefitsCount={pointsRewards.length}
            onBenefitsOpened={trackBenefitsOpened}
          >
            <Loyalty.InfoBenefits
              availablePoints={availablePoints}
              loyaltyInfoBenefits={pointsRewards}
              isLoading={isLoyaltyInfoLoading}
              hasError={hasFailedToLoad}
              style={benefitsStyle}
              outerContainerStyle={styles.loyaltyInfoBenefitsOuterContainer}
              disclaimerPosition="inside"
              disclaimer={benefitsDisclaimer}
              refetch={refetchLoyaltyInfo}
            />
          </Loyalty.Home.ProgressBenefit>
        ) : null}

        {/* Point History & How It Works Buttons */}

        {!minWidth.isSM ? (
          <Loyalty.Home.Body isPremium={isPremium}>
            {minWidth.isSM ? (
              <Loyalty.PointsHistory.CtaButton
                palette={isPremium ? 'cream' : 'secondary'}
                onPress={showPointsHistoryModal}
              />
            ) : null}

            {currentBreakpoint.isXS ? (
              <>
                <Loyalty.PointsHistory.CtaLink
                  style={isPremium ? LOYALTY_PALETTES.premiumFgColor : undefined}
                  onPress={showPointsHistoryModal}
                />

                <BodyText
                  style={isPremium ? LOYALTY_PALETTES.premiumFgColor : undefined}
                  sizeMatch={['14']}
                >
                  •
                </BodyText>

                <Loyalty.HowItWorks.Cta
                  style={isPremium ? LOYALTY_PALETTES.premiumFgColor : undefined}
                  onPress={showHowItWorksModal}
                />
              </>
            ) : null}
          </Loyalty.Home.Body>
        ) : null}

        {/* Challenges & Rewards */}

        {currentBreakpoint.isXS ? (
          <>
            <LoyaltyHomeRewards
              style={rewardsStyleXS}
              hasRewards={hasRewards}
              rewards={rewards}
              showTerms={showTerms}
            />

            <LoyaltyHomeChallenges
              style={challengesStyleXS}
              hasChallenges={hasChallenges}
              isPremium={isPremium}
              isInsideModal={variation === 'modal'}
              challenges={challenges}
              startOrder={startOrder}
            />
          </>
        ) : null}

        {minWidth.isSM ? (
          <>
            <LoyaltyHomeChallenges
              style={[premiumBgColor, styles.challengesPaddingSM]}
              hasChallenges={hasChallenges}
              isPremium={isPremium}
              isInsideModal={variation === 'modal'}
              challenges={challenges}
              startOrder={startOrder}
            />

            <View ref={rewardsTargetRef} />

            <LoyaltyHomeRewards
              style={[premiumBgColor, premiumBorder, styles.rewardsPaddingSM]}
              hasRewards={hasRewards}
              rewards={rewards}
              showTerms={showTerms}
            />
          </>
        ) : null}

        {/* FAQ (XS Only) */}

        {currentBreakpoint.isXS && faqLoyaltyHome.length > 0 ? (
          <Loyalty.Home.FAQ
            style={faqStyleXS}
            title={faqTitleXs}
            subtitle={faqSubtitleXs}
            palette={isPremium ? 'dark' : 'light'}
            showHowItWorksModal={showHowItWorksModal}
            onForgotToScan={isForgotToScanEnabled ? goToForgotToScan : undefined}
          >
            {faqLoyaltyHome.map((entry) => (
              <Loyalty.Home.FAQEntry
                key={entry.title}
                entry={entry}
                palette={isPremium ? 'cream' : 'kale'}
              />
            ))}
          </Loyalty.Home.FAQ>
        ) : null}

        {/* Unlockable Rewards (SM+ Only) */}

        {minWidth.isSM ? (
          <Loyalty.Home.UnlockableRewards
            isPremium={isPremium}
            title={unlockableRewardsTitle}
            subtitle={unlockableRewardsSubtitle}
            totalPoints={totalPoints}
            onOrderToEarn={startOrder}
            onForgotToScan={goToForgotToScan}
            onPointsHistory={showPointsHistoryModal}
          >
            <Loyalty.Home.RewardsGrid
              availablePoints={availablePoints}
              pointRewards={pointsRewards}
              onStartOrder={startOrder}
            />
          </Loyalty.Home.UnlockableRewards>
        ) : null}

        {/* How It Works (SM+ Only) */}

        {minWidth.isSM && !featuredReward ? (
          <Loyalty.Home.HowItWorksSection
            entries={homeHowItWorksSM}
            disclaimer={homeHowItWorksDisclaimer}
            isPremium={isPremium}
            termsAndConditionsUrl={termsAndConditionsUrl}
            onTermsLinkPress={trackOnLinkPress}
          />
        ) : undefined}

        {/* FAQ (SM and Above Only) */}

        {minWidth.isSM && faqLoyaltyHome.length > 0 ? (
          <Loyalty.Home.FAQ
            title={faqTitleSm}
            subtitle={faqSubtitleSm}
            palette={isPremium ? 'dark' : 'light'}
          >
            {faqLoyaltyHome.map((entry) => (
              <Loyalty.Home.FAQEntry
                key={entry.title}
                entry={entry}
                palette={isPremium ? 'cream' : 'kale'}
              />
            ))}
          </Loyalty.Home.FAQ>
        ) : null}

        {/* App Footer */}

        {minWidth.isSM ? <AppFooter /> : null}
        {shouldApplyTabNavbarPadding ? <View style={navbarPaddingBottomStyle} /> : null}

        {/* Modals */}

        <LoyaltyPointHistoryModal
          availablePoints={availablePoints}
          visible={isShowingPointsHistoryModal}
          startOrder={startOrder}
          onRequestClose={hidePointsHistoryModal}
        />

        <LoyaltyHowItWorksModal
          visible={isShowingHowItWorksModal}
          onRequestClose={hideHowItWorksModal}
        />

        <ModalDialogue visible={Boolean(termsBeingShown)} onRequestClose={hideTerms}>
          {termsBeingShown?.terms && termsBeingShown?.name ? (
            <LoyaltyRewardTerms
              title={termsBeingShown.name}
              terms={termsBeingShown.terms}
              expirationDate={termsBeingShown?.expirationDate}
            />
          ) : null}
        </ModalDialogue>
      </Loyalty.Home.Container>

      {shouldRenderModalNavbarBackButton ? <ModalNavbarBackButton /> : null}
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  challengesRewardsPaddingXS: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['6'],
  },
  challengesPaddingSM: {
    paddingTop: theme.spacing['20'],
    paddingBottom: theme.spacing['10'],
  },
  rewardsPaddingSM: {
    paddingTop: theme.spacing['10'],
    paddingBottom: theme.spacing['20'],
  },
  paddingWithContentTop: {
    paddingTop: theme.spacing['2'],
  },
  paddingWithContentBottom: {
    paddingBottom: theme.spacing['2'],
  },
  premiumBenefitsBorder: {
    borderColor: theme.colors.CREAM,
  },
  loyaltyInfoBenefitsOuterContainer: {
    marginTop: 0,
  },
  loyaltyInfoBenefits: {
    marginTop: 0,
    marginBottom: 0,
    marginHorizontal: theme.spacing['6'],
    borderTopWidth: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    paddingBottom: theme.spacing['4'],
  },
  faqContainerSM: {
    alignItems: 'center',
    paddingHorizontal: theme.spacing['30'],
    paddingVertical: theme.spacing['20'],
  },
  featuredRewardNavbarPadding: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  premiumBorder: {
    borderColor: theme.colors.CREAM,
    borderBottomWidth: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  variation?: Variation;
};

type SignedInProps = {
  variation: Variation;
};

type Variation = 'modal' | 'tab';
